<template>
  <v-card>
    <v-card-title class="d-flex align-center">
      <span>Move: {{ institutionData.title }}</span>
    </v-card-title>
    <v-divider />
    <v-card-text class="pa-6">
      <v-row  v-if="error">
        <v-col>
          <v-alert density="compact" v-if="error" type="error" class="mb-4"> {{ error }}</v-alert>
        </v-col>
      </v-row>

      <v-alert
        density="comfortable"
        type="info"
        variant="tonal"
        class="mb-4"
        icon="fa fa-info-circle"
      >
        Moving an institution will transfer all its cohorts, users, and access codes to the selected tenant.
      </v-alert>

      <v-card
        variant="outlined"
        class="mb-4"
      >
        <v-card-text class="pa-4">
          <div class="d-flex align-center mb-3">
            <v-chip
              color="primary"
              variant="flat"
              size="small"
              class="mr-2"
            >
              1
            </v-chip>
            <span class="text-h6">Select Tenant</span>
          </div>

          <tenant-search-autocomplete
            v-model="selectedTenant"
            entity-type="tenant"
          />
          <v-btn
            v-if="!dryRunResult"
            class="mt-4"
            color="primary"
            variant="tonal"
            prepend-icon="far fa-magnifying-glass"
            @click="moveInstitution"
            :disabled="!selectedTenant || isSubmitting"
            :loading="isSubmitting"
            block
          >
            Test Impact
          </v-btn>
        </v-card-text>
      </v-card>
      <v-expand-transition>
        <v-card
          v-if="dryRunResult"
          color="error"
          variant="outlined"
          class="mb-4"
        >
          <v-card-text class="pa-4">
            <div class="d-flex align-center mb-3">
              <span class="text-h6 text-error">Danger Zone</span>
              <v-chip class="ml-2" color="error" variant="tonal" size="small">
                This action cannot be undone
              </v-chip>
            </div>

            <v-alert
              type="error"
              variant="tonal"
              density="comfortable"
              class="mb-4"
            >
              <div class="font-weight-medium mb-2">This move will affect the following:</div>
              <v-row dense>
                <v-col cols="auto">
                  <v-chip color="error" variant="flat" size="small">
                    <v-icon start size="small">far fa-users</v-icon>
                    Users: {{ dryRunResult.data.result.usersCount }}
                  </v-chip>
                </v-col>
                <v-col cols="auto">
                  <v-chip color="error" variant="flat" size="small">
                    <v-icon start size="small">far fa-ticket</v-icon>
                    Access Codes: {{ dryRunResult.data.result.accessCodesCount }}
                  </v-chip>
                </v-col>
                <v-col cols="auto">
                  <v-chip color="error" variant="flat" size="small">
                    <v-icon start size="small">far fa-people-group</v-icon>
                    Cohorts: {{ dryRunResult.data.result.institutionCohorts }}
                  </v-chip>
                </v-col>
              </v-row>
            </v-alert>

            <danger-confirmation-field
              v-model="confirmDanger"
              message=""
              confirm-string="CONFIRM"
              :action-label="$t('common.action_label')"
            />
          </v-card-text>
        </v-card>
      </v-expand-transition>
    </v-card-text>
    <v-divider />
    <v-card-actions class="pa-4">
      <v-spacer/>
      <v-btn
        variant="text"
        @click="$emit('close')"
      >
        {{ $t("buttons.cancel") }}
      </v-btn>
      <v-btn
        color="primary"
        variant="flat"
        prepend-icon="far fa-check"
        @click="moveInstitution(false)"
        v-bind:disabled="!confirmDanger || isSubmitting"
        :loading="isSubmitting"
      >
        {{ $t("buttons.confirm") }} Move
      </v-btn>
    </v-card-actions>
  </v-card>
</template>

<script setup lang="ts">
import {PropType, ref, watch} from "vue";
import yapi from "@/lib/yapi";
import {Institution, isYapiError, YapiError} from "@YenzaCT/sdk";
import {useGlobalStore} from "@/store";
import {useRoute} from "vue-router";
import DangerConfirmationField from "@/components/DangerConfirmationField.vue";
import TenantSearchAutocomplete from "@/components/FormFields/TenantSearchAutocomplete.vue";

const store = useGlobalStore();
const route = useRoute();

const confirmDanger = ref<boolean>(false);
const institutionId = route.params.institutionId as string;
const selectedTenant = ref<string | undefined>();
const dryRunResult = ref();
const error = ref<string | undefined>("");
const isSubmitting = ref<boolean>(false);

const props = defineProps({
  institution: {
    type: Object as PropType<Institution>,
    required: true
  }
});

const institutionData = ref<Institution>({...props.institution});

const emits = defineEmits<{
  (e: "close"): void;
}
>();

watch(selectedTenant, () => {
  dryRunResult.value = undefined; // Reset dryRunResult when selectedTenant changes
});

const moveInstitution = async (dryRun: boolean) => {
  try {
    isSubmitting.value = true;
    store.networkBusy = true;

    if (!selectedTenant.value || !selectedTenant.value) {
      error.value = "Please select a tenant before proceeding.";
      return;
    }

    if (dryRun) {
      dryRunResult.value = await yapi.admin.institution.move(institutionId, selectedTenant.value);
      return;
    } else {
      await yapi.admin.institution.move(institutionId, selectedTenant.value, false);
      emits("close");
      store.showSnackbar("Institution has been moved to " + selectedTenant.value);
    }
  } catch (e) {
    if (isYapiError(e)) {
      const yError = e as YapiError;
      await store.handleYapiError(yError);
    } else {
      throw e;
    }
  } finally {
    isSubmitting.value = false;
    store.networkBusy = false;
  }
};

</script>

<style scoped>
.v-card {
  overflow: visible;
}

.v-chip {
  font-weight: 500;
}

.text-h6 {
  font-size: 1.1rem !important;
}
</style>
